import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormState('success');
      form.reset();
      setTimeout(() => setFormState('idle'), 3000);
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const res = await fetch('http://zhidavip.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = parsed?.code as string | undefined;

      if (res.ok && code === 'OK') {
        setFormState('success');
        form.reset();
        setTimeout(() => setFormState('idle'), 3000);
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || '';
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <Navbar />
      <Breadcrumb />
      <main>
        <section className="flex flex-col lg:flex-row">
          <div className="lg:w-3/5 bg-background-50 px-8 md:px-16 py-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-10">
              关于韩国漫画网
            </h1>

            <div className="grid grid-cols-3 gap-8">
              <div className="border-r border-background-300 pr-6">
                <p className="font-heading text-5xl font-black text-secondary-500 mb-2">5000<span className="text-3xl">+</span></p>
                <p className="text-sm text-foreground-600 leading-relaxed">收录韩漫<br />作品数量</p>
              </div>
              <div className="border-r border-background-300 pr-6">
                <p className="font-heading text-5xl font-black text-secondary-500 mb-2">80万<span className="text-3xl">+</span></p>
                <p className="text-sm text-foreground-600 leading-relaxed">每日活跃<br />读者用户</p>
              </div>
              <div>
                <p className="font-heading text-5xl font-black text-secondary-500 mb-2">99<span className="text-3xl">%</span></p>
                <p className="text-sm text-foreground-600 leading-relaxed">读者满意<br />度评价</p>
              </div>
            </div>
          </div>

          <div className="lg:w-2/5 bg-secondary-100/40 relative overflow-hidden min-h-[300px] lg:min-h-[400px] flex items-center justify-center">
            <div className="absolute inset-0 flex flex-wrap gap-2 p-8 opacity-30">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-secondary-300/60"
                  style={{
                    width: `${60 + (i % 5) * 30}px`,
                    height: `${60 + (i % 4) * 20}px`,
                  }}
                />
              ))}
            </div>
            <p className="relative font-heading text-4xl md:text-5xl font-bold text-secondary-700 text-center leading-tight z-10">
              韩漫在线观看<br />漫画免费下拉式阅读
            </p>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
                <span className="text-accent-600">品牌历程</span> · 用户信赖
              </h2>
              <p className="text-foreground-500 mt-3 text-sm md:text-base max-w-2xl mx-auto">
                韩国漫画网致力于为广大韩漫爱好者提供最优质、最便捷的漫画免费下拉式阅读服务
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 max-w-7xl mx-auto">
              <div className="lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5] group">
                  <img
                    src="https://readdy.ai/api/search-image?query=Creative%20team%20workspace%20with%20manga%20illustrations%20on%20walls%20warm%20ambient%20lighting%20modern%20Korean%20office%20environment%20artistic%20atmosphere%20collaborative%20space&width=900&height=1125&seq=about-team&orientation=portrait"
                    alt="韩国漫画网团队"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-medium mb-2 whitespace-nowrap">
                      我们的团队
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-white">热爱漫画，更热爱分享</h3>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2 flex flex-col gap-4">
                <div className="rounded-2xl bg-accent-100/60 p-8 flex-1 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-200 text-accent-800 text-xs font-medium mb-3 whitespace-nowrap w-fit">
                    企业愿景
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-3">让每一位读者都能畅享韩漫</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    我们希望通过技术手段打破阅读壁垒，为全球华语读者提供最流畅的韩漫在线观看体验。好看的韩国漫画全集，尽在韩国漫画网。
                  </p>
                </div>

                <div className="rounded-2xl bg-secondary-100/60 p-8 flex-1 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 rounded-full bg-secondary-200 text-secondary-800 text-xs font-medium mb-3 whitespace-nowrap w-fit">
                    核心价值
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-3">免费 · 高清 · 完整阅读</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    我们坚持免费提供所有漫画内容，采用下拉式阅读模式让阅读更加自然流畅。完整阅读体验是我们的核心追求。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-chat-quote-line text-accent-600 text-sm w-4 h-4 flex items-center justify-center" />
                </span>
                <span className="text-xs text-foreground-500 font-medium whitespace-nowrap">读者评价</span>
              </div>
              <h2 className="font-heading text-3xl font-bold text-foreground-950">真实阅读反馈</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {reviews.map((review, i) => (
                <div key={review.id} className={`bg-background-50 rounded-2xl p-6 ${
                  i === 2 ? 'md:row-span-2 bg-secondary-100/60' : ''
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                  <i className="ri-double-quotes-l text-accent-400 text-xl block mb-3" />
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-16 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 max-w-6xl mx-auto">
              <div className="lg:w-1/2">
                <h2 className="font-heading text-3xl font-bold text-foreground-950 mb-6">联系我们</h2>
                <p className="text-foreground-500 mb-8 text-sm md:text-base leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在24小时内回复您的消息。
                </p>

                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <p className="text-sm text-foreground-500">hello@zhidavip.com</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-secondary-600 text-lg w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                      <p className="text-sm text-foreground-500">韩国漫画网</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京科技园区</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2">
                <form
                  onSubmit={handleSubmit}
                 
                  className="bg-background-100 rounded-2xl p-6 md:p-8 flex flex-col gap-5"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    />
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="bg-primary-500 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formState === 'submitting' ? '提交中...' : '发送留言'}
                  </button>

                  {formState === 'success' && (
                    <p className="text-accent-600 text-sm text-center">留言发送成功！我们会尽快回复您。</p>
                  )}
                  {formState === 'error' && formError && (
                    <p className="text-red-500 text-sm text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-foreground-950 text-center mb-10">常见问题</h2>
            <div className="flex flex-col gap-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-2xl overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-5 cursor-pointer font-medium text-foreground-800 text-sm md:text-base">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-lg text-foreground-500 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center" />
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于韩国漫画网 - 韩漫在线观看",
          "description": "韩国漫画网致力于为广大韩漫爱好者提供最优质、最便捷的漫画免费下拉式阅读服务。",
          "url": "http://zhidavip.com/about",
          "mainEntity": {
            "@type": "Organization",
            "name": "韩国漫画网",
            "url": "http://zhidavip.com/",
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "hello@zhidavip.com",
              "contactType": "customer service"
            }
          }
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })
      }} />
    </>
  );
}